// Paystack Integration Utilities
// Using configuration from backend API

const PAYSTACK_SCRIPT_URL = 'https://js.paystack.co/v1/inline.js';

/**
 * Load Paystack inline script dynamically
 * @returns {Promise<void>}
 */
export const loadPaystackScript = () => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.PaystackPop) {
      console.log('[Paystack] Script already loaded');
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector(`script[src="${PAYSTACK_SCRIPT_URL}"]`);
    if (existingScript) {
      console.log('[Paystack] Script already loading, waiting...');
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Paystack script')));
      return;
    }

    // Create and load script
    const script = document.createElement('script');
    script.src = PAYSTACK_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      console.log('[Paystack] Script loaded successfully');
      resolve();
    };
    script.onerror = () => {
      console.error('[Paystack] Failed to load script');
      reject(new Error('Failed to load Paystack script'));
    };
    document.body.appendChild(script);
  });
};

/**
 * Initialize Paystack payment popup
 * @param {Object} config - Paystack configuration
 * @param {string} config.publicKey - Paystack public key
 * @param {string} config.email - Customer email
 * @param {number} config.amount - Amount in GHS (will be converted to pesewas)
 * @param {string} config.reference - Unique transaction reference
 * @param {string} config.currency - Currency code (default: GHS)
 * @param {Object} config.metadata - Additional metadata
 * @param {Function} config.onSuccess - Success callback
 * @param {Function} config.onClose - Close callback
 * @returns {Object} Paystack handler instance
 */
export const initializePaystack = (config) => {
  if (!window.PaystackPop) {
    throw new Error('Paystack script not loaded. Call loadPaystackScript() first.');
  }

  if (!config.publicKey) {
    throw new Error('Paystack public key is required');
  }

  if (!config.email) {
    throw new Error('Customer email is required');
  }

  if (!config.amount || config.amount <= 0) {
    throw new Error('Valid amount is required');
  }

  // Convert amount to pesewas (smallest currency unit)
  const amountInPesewas = Math.round(config.amount * 100);

  console.log('[Paystack] Initializing payment:', {
    email: config.email,
    amount: config.amount,
    amountInPesewas: amountInPesewas,
    currency: config.currency || 'GHS',
    reference: config.reference
  });

  return window.PaystackPop.setup({
    key: config.publicKey,
    email: config.email,
    amount: amountInPesewas,
    currency: config.currency || 'GHS',
    ref: config.reference,
    metadata: config.metadata || {},
    callback: function(response) {
      console.log('[Paystack] Payment successful:', response);
      if (config.onSuccess) {
        config.onSuccess(response);
      }
    },
    onClose: function() {
      console.log('[Paystack] Popup closed');
      if (config.onClose) {
        config.onClose();
      }
    }
  });
};

/**
 * Verify Paystack transaction
 * @param {string} reference - Transaction reference
 * @param {string} secretKey - Paystack secret key (server-side only)
 * @returns {Promise<Object>} Transaction details
 */
export const verifyTransaction = async (reference, secretKey) => {
  // This should be called from the backend only
  const response = await fetch(`https://api.paystack.co/transaction/verify/${reference}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${secretKey}`,
      'Content-Type': 'application/json'
    }
  });

  if (!response.ok) {
    throw new Error('Failed to verify transaction');
  }

  return await response.json();
};

// Default configuration
export const PAYSTACK_CONFIG = {
  publicKey: 'pk_test_61a1642eae929d496529c342f6f95ae3b05bd89c', // Test key
  currency: 'GHS',
  currencySymbol: 'GH₵'
};

export default {
  loadPaystackScript,
  initializePaystack,
  verifyTransaction,
  PAYSTACK_CONFIG
};
